const STORAGE_KEY = 'lonewolf.audio.v1';
const BASE_MUSIC_LEVEL = 0.48;
const COMBAT_MUSIC_LEVEL = 0.62;
const MAX_ACTIVE_VOICES = 32;
const CROSSFADE_POLL_MS = 25;
const CROSSFADE_START_TIMEOUT_MS = 420;
const INTERRUPT_FADE_SECONDS = 0.12;
const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_SECONDS = 0.2;
const SILENCE = 0.0001;
const TENSION_CUTOFF_MIN = 820;
const TENSION_CUTOFF_MAX = 20000;
const LIMITER_KNEE = 0.9;
const LIMITER_DOMAIN = 2;
const DUCK_ATTACK = 0.012;
const DUCK_HOLD = 0.18;
const DUCK_RELEASE = 0.28;
const LIMITER_CURVE_POINTS = 4097;
const PULSE_STEPS = 8;
const PULSE_RAMP_SECONDS = 0.35;
const PULSE_RATE_FLOOR = 0.25;
const PULSE_IDLE_INTENSITY = 0.02;
const DEFAULT_VOLUMES = { masterVolume: 0.9, musicVolume: 0.72, sfxVolume: 0.9 };

export const MOODS = {
  // Free-time regional beds use bpm only for synthesis fallback, never beat-locked overlays.
  // `bpm` is the tempo `scripts/generate_audio.py` rendered the cue at, not a rough mood hint: the
  // offline check decodes every file and requires its length to be a whole number of four-bar
  // cycles at this number, which is what keeps the intensity layer below in time with the bed.
  menu: { file: 'menu.opus', bpm: 60, root: 146.83, scale: [0, 3, 7, 10, 12], bass: 0.045, lead: 0.018, fade: 2.0, combat: false },
  village: { file: 'village.opus', bpm: 60, barAligned: false, root: 164.81, scale: [0, 2, 4, 7, 9], bass: 0.038, lead: 0.016, fade: 2.4, combat: false },
  eerie: { file: 'eerie.opus', bpm: 80, barAligned: false, root: 146.83, scale: [0, 1, 3, 7, 10], bass: 0.032, lead: 0.01, fade: 2.5, combat: false },
  haunted: { file: 'haunted.opus', bpm: 72, barAligned: false, root: 130.81, scale: [0, 1, 5, 7, 8], bass: 0.034, lead: 0.01, fade: 2.5, combat: false },
  exploration: { file: 'exploration.opus', bpm: 80, root: 164.81, scale: [0, 2, 3, 7, 10], bass: 0.04, lead: 0.016, fade: 1.6, combat: false },
  cutscene: { file: 'cutscene.opus', bpm: 80, root: 130.81, scale: [0, 2, 3, 7, 8, 10], bass: 0.05, lead: 0.02, fade: 2.2, combat: false },
  stealth: { file: 'stealth.opus', bpm: 72, root: 146.83, scale: [0, 2, 3, 7, 10], bass: 0.04, lead: 0.012, fade: 1.6, combat: false },
  battle: { file: 'battle.opus', bpm: 120, root: 110, scale: [0, 3, 5, 7, 10], bass: 0.055, lead: 0.024, fade: 1.2, combat: true },
  boss: { file: 'boss.opus', bpm: 90, root: 98, scale: [0, 1, 5, 7, 8], bass: 0.062, lead: 0.03, fade: 1.2, combat: true },
  victory: { file: 'victory.opus', bpm: 76, root: 174.61, scale: [0, 2, 4, 7, 9], bass: 0.045, lead: 0.02, fade: 1.8, combat: false },
  defeat: { file: 'defeat.opus', bpm: 54, root: 130.81, scale: [0, 1, 3, 7, 8], bass: 0.05, lead: 0.014, fade: 2.4, combat: false },
};

/**
 * Adaptive intensity layers. One loop per mood cannot say how close a hunt is or how bad the
 * bleeding has got: a stealth cue sounds identical whether a guard is ten paces behind you or half
 * a mile off, and so does a fight at full blood. These percussive layers ride the music bus — under
 * the same duck, tension filter and limiter as the cue — at the cue's own tempo, and each layer
 * only sounds once `intensity` clears its `from` floor. A second stem per mood would have cost
 * another megabyte of payload; this costs nothing and is the part that actually tracks state.
 */
export const PULSE_PROFILES = {
  exploration: {
    div: 2,
    gain: 0.5,
    layers: [
      { at: [0, 4], from: 0.05, voice: 'thump' },
      { at: [2, 6], from: 0.3, voice: 'tick' },
      { at: [1, 3, 5, 7], from: 0.72, voice: 'tick', gain: 0.55 },
    ],
  },
  stealth: {
    div: 1,
    gain: 0.66,
    // The heartbeat itself speeds up: a double-tap at a slow quarter is a resting pulse, the same
    // double-tap on eighths is a chase.
    rate: (intensity) => 1 + intensity * 1.1,
    layers: [
      { at: [0, 4], from: 0.04, voice: 'heart' },
      { at: [2, 6], from: 0.55, voice: 'tick', gain: 0.5 },
    ],
  },
  battle: {
    div: 4,
    gain: 0.52,
    layers: [
      { at: [0, 4], from: 0.04, voice: 'thump' },
      { at: [2, 6], from: 0.25, voice: 'tom' },
      { at: [1, 3, 5, 7], from: 0.5, voice: 'tick', gain: 0.45 },
    ],
  },
  boss: {
    div: 4,
    gain: 0.64,
    layers: [
      { at: [0, 3, 6], from: 0.04, voice: 'thump', gain: 1.1 },
      { at: [2, 5], from: 0.3, voice: 'tom' },
      { at: [1, 3, 5, 7], from: 0.6, voice: 'tick', gain: 0.55 },
    ],
  },
};

const PULSE_VOICES = {
  thump(api, config, level) {
    api.tone(config.root / 2, 0.24, 'sine', 0.16 * level, 0, 0);
    api.noise(0.03, 0.05 * level, 1700, 0);
  },
  tom(api, config, level) {
    api.tone(config.root * 1.5, 0.15, 'triangle', 0.07 * level, 0, 0);
  },
  tick(api, config, level) {
    api.noise(0.045, 0.05 * level, 3600, 0);
  },
  heart(api, config, level) {
    api.tone(config.root / 2, 0.2, 'sine', 0.2 * level, 0, 0);
    api.tone(config.root / 2.35, 0.15, 'sine', 0.13 * level, 0, 0.19);
  },
};

const SFX_ALIASES = {
  'heavy-swing': 'heavySwing',
  'hit-light': 'hitLight',
  'hit-heavy': 'hitHeavy',
  bossdown: 'bossDown',
  footsteps: 'footstep',
  step: 'footstep',
  uiConfirm: 'confirm',
  button: 'confirm',
  bloodlineChange: 'bloodline',
  'bloodline-change': 'bloodline',
};

const SFX_DUCK = {
  hitLight: 0.14,
  hitHeavy: 0.34,
  hurt: 0.3,
  parry: 0.18,
  magic: 0.12,
  howl: 0.3,
  bossDown: 0.42,
  dodge: 0.12,
  bloodline: 0.16,
  suspicion: 0.08,
  parryHit: 0.3,
  tell: 0.1,
};

const SFX_PLAYERS = {
  swing(api, amount) {
    api.noise(0.13, 0.065 * amount, 2900);
    api.tone(230, 0.1, 'triangle', 0.03 * amount, 105);
    api.tone(920, 0.06, 'sine', 0.014 * amount, -360, 0.035);
  },
  heavySwing(api, amount) {
    api.noise(0.24, 0.09 * amount, 1700);
    api.tone(145, 0.2, 'sawtooth', 0.045 * amount, 58);
    api.tone(580, 0.12, 'triangle', 0.025 * amount, -220, 0.06);
  },
  hitLight(api, amount) {
    api.noise(0.12, 0.13 * amount, 1450);
    api.tone(118, 0.12, 'square', 0.065 * amount, -42);
    api.tone(720, 0.07, 'triangle', 0.025 * amount, -280, 0.015);
  },
  hitHeavy(api, amount) {
    api.noise(0.22, 0.18 * amount, 820);
    api.tone(82, 0.22, 'square', 0.09 * amount, -34);
    api.tone(196, 0.13, 'sawtooth', 0.04 * amount, -80, 0.02);
  },
  blocked(api, amount) {
    api.noise(0.06, 0.07 * amount, 2800);
    api.tone(260, 0.08, 'square', 0.035 * amount, -70);
  },
  // A miss has to sound like air, not like a guard: the old cue reused `blocked`, which told the
  // player their swing had been parried when nothing had touched them.
  whiff(api, amount) {
    api.noise(0.17, 0.05 * amount, 1500, 0.06);
    api.tone(300, 0.16, 'sine', 0.016 * amount, -170, 0.05);
  },
  hurt(api, amount) {
    api.tone(150, 0.28, 'sawtooth', 0.08 * amount, -80);
    api.noise(0.18, 0.08 * amount, 500);
  },
  dodge(api, amount) {
    api.noise(0.15, 0.075 * amount, 4600);
    api.tone(360, 0.2, 'sine', 0.05 * amount, 360);
    api.tone(720, 0.09, 'triangle', 0.018 * amount, -180, 0.04);
  },
  pickup(api, amount) {
    api.tone(520, 0.35, 'sine', 0.06 * amount, 286);
    api.tone(780, 0.45, 'triangle', 0.035 * amount, 156, 0.08);
  },
  sigil(api, amount) {
    api.tone(392, 0.5, 'sine', 0.055 * amount, 0);
    api.tone(523.25, 0.5, 'triangle', 0.045 * amount, 0, 0.06);
    api.tone(783.99, 0.6, 'sine', 0.04 * amount, 0, 0.12);
    api.noise(0.4, 0.02 * amount, 3600, 0.05);
  },
  interact(api, amount) {
    api.tone(480, 0.08, 'triangle', 0.025 * amount, 120);
    api.tone(720, 0.12, 'sine', 0.02 * amount, 90, 0.045);
  },
  ui(api, amount) {
    api.tone(480, 0.08, 'triangle', 0.025 * amount, 120);
    api.tone(720, 0.12, 'sine', 0.02 * amount, 90, 0.045);
  },
  magic(api, amount) {
    api.tone(330, 0.35, 'sine', 0.06 * amount, 181.5);
    api.tone(495, 0.45, 'triangle', 0.035 * amount, 99, 0.08);
  },
  parry(api, amount) {
    api.noise(0.09, 0.1 * amount, 5200);
    api.tone(740, 0.22, 'triangle', 0.07 * amount, 420);
    api.tone(1180, 0.14, 'sine', 0.035 * amount, 180, 0.04);
  },
  // Raising the guard and catching a blade on it are two different events, and one sound for both
  // told the player their stance had been broken at the moment they earned it.
  guard(api, amount) {
    api.tone(300, 0.09, 'triangle', 0.038 * amount, 130);
    api.noise(0.07, 0.05 * amount, 2500);
  },
  // The windup tell. It has to arrive before the strike does, and it has to sound like something
  // is about to happen rather than like something already has.
  tell(api, amount) {
    api.tone(500, 0.11, 'sawtooth', 0.032 * amount, 240);
    api.tone(760, 0.13, 'sawtooth', 0.03 * amount, 300, 0.055);
    api.noise(0.15, 0.026 * amount, 1900, 0.02);
  },
  // A caught blade: metal first, then the body of the note underneath it.
  parryHit(api, amount) {
    api.noise(0.1, 0.14 * amount, 6400);
    api.tone(880, 0.26, 'triangle', 0.085 * amount, 620);
    api.tone(1760, 0.16, 'sine', 0.045 * amount, 260, 0.03);
    api.tone(196, 0.18, 'square', 0.05 * amount, -60, 0.02);
  },
  howl(api, amount) {
    api.tone(122, 0.9, 'sawtooth', 0.07 * amount, -40);
    api.noise(0.5, 0.045 * amount, 340);
  },
  bossDown(api, amount) {
    api.tone(82, 0.9, 'sawtooth', 0.07 * amount, -40);
    api.noise(0.5, 0.045 * amount, 340);
  },
  alert(api, amount) {
    api.tone(680, 0.12, 'square', 0.05 * amount, 240);
    api.tone(920, 0.16, 'square', 0.045 * amount, -180, 0.1);
  },
  suspicion(api, amount) {
    api.tone(540, 0.09, 'triangle', 0.028 * amount, 0);
    api.tone(720, 0.13, 'triangle', 0.026 * amount, 0, 0.11);
  },
  down(api, amount) {
    api.tone(170, 0.32, 'triangle', 0.06 * amount, -70);
  },
  footstep(api, amount) {
    const pitch = 920 + amount * 420;
    api.noise(0.065, 0.038 * amount, pitch);
    api.tone(96 + amount * 24, 0.075, 'sine', 0.022 * amount, 24);
  },
  confirm(api, amount) {
    api.tone(620, 0.07, 'sine', 0.035 * amount, 90);
    api.tone(930, 0.1, 'triangle', 0.02 * amount, 60, 0.05);
  },
  bloodline(api, amount) {
    api.tone(110, 0.5, 'sawtooth', 0.06 * amount, 70);
    api.tone(220, 0.42, 'triangle', 0.035 * amount, 110, 0.12);
    api.noise(0.35, 0.025 * amount, 280);
  },
};

function canPlayAudio() {
  return typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
}

function clamp01(value, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(0, Math.min(1, number));
}

function note(root, scale, index) {
  return root * (2 ** (scale[index % scale.length] / 12));
}

function cutoffForTension(tension) {
  return TENSION_CUTOFF_MAX * (TENSION_CUTOFF_MIN / TENSION_CUTOFF_MAX) ** clamp01(tension, 0);
}

function softClip(value) {
  const magnitude = Math.abs(value);
  if (magnitude <= LIMITER_KNEE) return value;
  const over = (magnitude - LIMITER_KNEE) / (1 - LIMITER_KNEE);
  return Math.sign(value) * (LIMITER_KNEE + (1 - LIMITER_KNEE) * Math.tanh(over));
}

export function createLimiter(context) {
  const compressor = context.createDynamicsCompressor();
  compressor.threshold.value = -1;
  compressor.knee.value = 0;
  compressor.ratio.value = 20;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.25;
  const shaper = context.createWaveShaper();
  const curve = new Float32Array(LIMITER_CURVE_POINTS);
  for (let index = 0; index < LIMITER_CURVE_POINTS; index += 1) curve[index] = softClip(-1 + (2 * index) / (LIMITER_CURVE_POINTS - 1));
  shaper.curve = curve;
  shaper.oversample = 'none';
  compressor.connect(shaper);
  return { compressor, shaper, output: shaper, knee: LIMITER_KNEE, ceiling: softClip(1) };
}

/** Equal-power fade curves for a crossfade of `duration` seconds. `out` falls 1→0 while `in` rises 0→1. */
export function equalPowerCurves(duration, sampleRate) {
  const length = Math.max(2, Math.round(Math.max(0.01, duration) * sampleRate));
  const out = new Float32Array(length);
  const incoming = new Float32Array(length);
  for (let index = 0; index < length; index += 1) {
    const phase = (index / (length - 1)) * Math.PI * 0.5;
    out[index] = Math.cos(phase);
    incoming[index] = Math.sin(phase);
  }
  return { out, in: incoming, length };
}

/**
 * Shared output bus: voice/music input and sfx input, tension filter, music ducking,
 * master gain, and a soft-clip limiter so the summed mix can never clip.
 */
export function createAudioGraph(context, options = {}) {
  const limiter = createLimiter(context);
  const master = context.createGain();
  const musicBus = context.createGain();
  const sfxBus = context.createGain();
  const tensionFilter = context.createBiquadFilter();
  const duck = context.createGain();

  tensionFilter.type = 'lowpass';
  tensionFilter.Q.value = 0.4;
  duck.gain.value = 1;

  musicBus.connect(tensionFilter);
  tensionFilter.connect(duck);
  duck.connect(master);
  sfxBus.connect(master);
  master.connect(limiter.compressor);
  limiter.output.connect(context.destination);

  const volumes = {
    masterVolume: clamp01(options.masterVolume, DEFAULT_VOLUMES.masterVolume),
    musicVolume: clamp01(options.musicVolume, DEFAULT_VOLUMES.musicVolume),
    sfxVolume: clamp01(options.sfxVolume, DEFAULT_VOLUMES.sfxVolume),
  };
  let muted = Boolean(options.muted);
  let combat = false;
  let tension = clamp01(options.tension, 0);
  let duckPeak = 0;
  let duckHoldUntil = 0;
  let duckReleaseAt = 0;

  const musicLevel = () => (combat ? COMBAT_MUSIC_LEVEL : BASE_MUSIC_LEVEL) * volumes.musicVolume;

  /** Depth of the duck in effect at `now`. A duck is a transient, not a level: without the release
   * below, the first heavy hit left every later cue sitting several dB under the bed. */
  function duckDepthAt(now) {
    if (duckPeak <= 0) return 0;
    if (now < duckHoldUntil) return duckPeak;
    if (now >= duckReleaseAt) return 0;
    const decayed = duckPeak * Math.exp(-(now - duckReleaseAt) / DUCK_RELEASE);
    return decayed < 0.0005 ? 0 : decayed;
  }

  function applyMaster(immediate = false) {
    const target = muted ? 0 : volumes.masterVolume;
    if (immediate) master.gain.value = target;
    else master.gain.setTargetAtTime(target, context.currentTime, 0.04);
  }
  function applyMusic(immediate = false) {
    const target = musicLevel();
    if (immediate) musicBus.gain.value = target;
    else musicBus.gain.setTargetAtTime(target, context.currentTime, 0.18);
  }
  function applySfx(immediate = false) {
    if (immediate) sfxBus.gain.value = volumes.sfxVolume;
    else sfxBus.gain.setTargetAtTime(volumes.sfxVolume, context.currentTime, 0.04);
  }
  function applyTension() {
    tensionFilter.frequency.setTargetAtTime(cutoffForTension(tension), context.currentTime, 0.35);
  }

  applyMaster(true);
  applyMusic(true);
  applySfx(true);
  applyTension();

  return {
    context,
    limiter,
    master,
    musicBus,
    sfxBus,
    musicInput: musicBus,
    sfxInput: sfxBus,
    limiterKnee: LIMITER_KNEE,
    limiterCeiling: limiter.ceiling,
    get values() {
      return { ...volumes, muted, combat, tension, ducking: duckDepthAt(context.currentTime) };
    },
    setMasterVolume(value) {
      volumes.masterVolume = clamp01(value, volumes.masterVolume);
      applyMaster();
      return volumes.masterVolume;
    },
    setMusicVolume(value) {
      volumes.musicVolume = clamp01(value, volumes.musicVolume);
      applyMusic();
      return volumes.musicVolume;
    },
    setSfxVolume(value) {
      volumes.sfxVolume = clamp01(value, volumes.sfxVolume);
      applySfx();
      return volumes.sfxVolume;
    },
    setMuted(value) {
      muted = Boolean(value);
      applyMaster();
      return muted;
    },
    setCombat(value) {
      combat = Boolean(value);
      applyMusic();
      return combat;
    },
    setTension(value) {
      const next = clamp01(value, tension);
      if (Math.abs(next - tension) < 0.01) return tension;
      tension = next;
      applyTension();
      return tension;
    },
    /** Pull the music bed down so a loud transient reads, then hand the level back to the bed. */
    duckMusic(amount, delay = 0) {
      const depth = clamp01(amount, 0);
      if (depth <= 0) return duckDepthAt(context.currentTime);
      const now = context.currentTime;
      const wait = Math.max(0, Number(delay) || 0);
      const from = duckDepthAt(now);
      if (wait === 0 && depth <= from) return from;
      const start = now + wait;
      duckPeak = depth;
      duckHoldUntil = start + DUCK_HOLD;
      duckReleaseAt = duckHoldUntil;
      if (wait === 0) {
        duck.gain.cancelScheduledValues(now);
        duck.gain.setValueAtTime(Math.max(SILENCE, 1 - from), now);
      }
      duck.gain.setTargetAtTime(1 - depth, start, DUCK_ATTACK);
      duck.gain.setTargetAtTime(1, start + DUCK_HOLD, DUCK_RELEASE);
      return depth;
    }
  };
}

/**
 * The adaptive layer. It owns one gain on the music bus, keeps its own step clock, and turns
 * `intensity` into both a level and a layer count, so a state change is heard twice: the bed
 * gains a heartbeat and the heartbeat gets faster. Scheduling is pull-based (`schedule`) instead of
 * timer-based so the director can drive it from the same lookahead clock as the fallback
 * sequencer, and so an OfflineAudioContext can render exactly the code that ships.
 */
export function createPulseLayer(context, options = {}) {
  const input = context.createGain();
  input.gain.value = SILENCE;
  if (options.destination) input.connect(options.destination);
  const api = {
    at: 0,
    tone(frequency, duration, type, value, slide, delay = 0) {
      options.voiceTone?.(frequency, duration, type, value, slide, api.at + delay);
    },
    noise(duration, value, filterFrequency, delay = 0) {
      options.voiceNoise?.(duration, value, filterFrequency, api.at + delay);
    },
  };
  let mood = null;
  let intensity = 0;
  let level = 0;
  let steps = 0;
  let notes = 0;

  const profile = () => (mood ? PULSE_PROFILES[mood] : null) || null;

  function levelFor(intensityAt, active = profile()) {
    if (!active) return 0;
    // Squared, so the bottom half of the range is a suggestion rather than a layer of its own.
    const pressure = Math.min(1, Math.max(0, intensityAt) * 1.6);
    return active.gain * pressure * pressure;
  }

  function apply(immediate = false) {
    const target = Math.max(SILENCE, levelFor(intensity));
    level = target;
    if (immediate) input.gain.setValueAtTime(target, context.currentTime);
    else input.gain.setTargetAtTime(target, context.currentTime, PULSE_RAMP_SECONDS);
    return target;
  }

  function setMood(next) {
    mood = PULSE_PROFILES[next] ? next : null;
    apply();
    return mood;
  }

  function setIntensity(value) {
    intensity = clamp01(value, intensity);
    apply();
    return intensity;
  }

  function secondsPerStep(intensityAt = intensity) {
    const active = profile();
    if (!active) return Infinity;
    const rate = active.rate ? active.rate(clamp01(intensityAt, 0)) : 1;
    return 60 / MOODS[mood].bpm / active.div / Math.max(PULSE_RATE_FLOOR, rate);
  }

  /** Sound every layer of the current bar position that this intensity has earned. */
  function schedule(step, startAt) {
    const active = profile();
    if (!active || !options.voiceTone || !options.voiceNoise) return 0;
    const pressure = clamp01(intensity, 0);
    if (pressure <= PULSE_IDLE_INTENSITY) return 0;
    const index = ((step % PULSE_STEPS) + PULSE_STEPS) % PULSE_STEPS;
    const config = MOODS[mood];
    api.at = startAt;
    let count = 0;
    for (const entry of active.layers) {
      if (pressure < entry.from || !entry.at.includes(index)) continue;
      PULSE_VOICES[entry.voice](api, config, (entry.gain ?? 1) * Math.min(1, pressure * 1.6));
      count += 1;
    }
    if (count === 0) return 0;
    steps += 1;
    notes += count;
    return count;
  }

  function dispose() {
    try { input.disconnect(); } catch {}
    mood = null;
  }

  return {
    input,
    setMood,
    setIntensity,
    secondsPerStep,
    schedule,
    dispose,
    levelFor,
    get mood() { return mood; },
    get intensity() { return intensity; },
    get level() { return level; },
    get steps() { return steps; },
    get notes() { return notes; },
  };
}

function safeStorage() {
  try {
    const store = typeof window !== 'undefined' ? window.localStorage : null;
    if (!store) return null;
    const probe = 'lonewolf.audio.probe';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

function readPersisted(store) {
  const volumes = { ...DEFAULT_VOLUMES, muted: false };
  if (!store) return { ...volumes, restored: false };
  try {
    const raw = JSON.parse(store.getItem(STORAGE_KEY) || 'null');
    if (!raw || typeof raw !== 'object') return { ...volumes, restored: false };
    const restored = ['masterVolume', 'musicVolume', 'sfxVolume', 'muted'].some((key) => key in raw);
    return {
      masterVolume: clamp01(raw.masterVolume, volumes.masterVolume),
      musicVolume: clamp01(raw.musicVolume, volumes.musicVolume),
      sfxVolume: clamp01(raw.sfxVolume, volumes.sfxVolume),
      muted: Boolean(raw.muted),
      restored,
    };
  } catch {
    return { ...volumes, restored: false };
  }
}

export function createAudioDirector(options = {}) {
  const store = options.storage === undefined ? safeStorage() : options.storage;
  const persisted = readPersisted(store);
  let context = null;
  let graph = null;
  let pulseLayer = null;
  let pulseTimer = null;
  let pulseStep = 0;
  let nextPulseTime = 0;
  let fallbackGain = null;
  let currentTrack = null;
  let currentMood = 'menu';
  let unlocked = false;
  let disposed = false;
  let restored = persisted.restored;
  let backgroundedFlag = false;
  let transitionToken = 0;
  let pollTimer = null;
  let schedulerTimer = null;
  let nextStepTime = 0;
  let fallbackStep = 0;
  let transitionTimers = new Set();
  let noiseBuffer = null;
  let noiseState = 0x6d2b79f5;
  let noiseCursor = 0;
  let sfxCount = 0;
  let lastSfx = null;
  let lastSfxIntensity = 0;
  let moodListener = null;
  let volumeListener = null;
  let AudioConstructor = null;
  const tracks = new Map();
  const activeVoices = new Set();

  function persist() {
    if (!store) return;
    const values = graph ? graph.values : { ...persisted };
    try {
      store.setItem(
        STORAGE_KEY,
        JSON.stringify({
          masterVolume: values.masterVolume,
          musicVolume: values.musicVolume,
          sfxVolume: values.sfxVolume,
          muted: values.muted,
        }),
      );
    } catch {}
  }

  function notifyVolumes() {
    if (!graph) return;
    const values = graph.values;
    volumeListener?.({ masterVolume: values.masterVolume, musicVolume: values.musicVolume, sfxVolume: values.sfxVolume, muted: values.muted });
  }

  function nextNoise() {
    noiseState = (Math.imul(1664525, noiseState) + 1013904223) >>> 0;
    return noiseState / 2_147_483_647.5 - 1;
  }

  function ensureContext() {
    if (disposed || !canPlayAudio()) return null;
    if (context) return context;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    try {
      context = new AudioContextClass();
      graph = createAudioGraph(context, {
        masterVolume: persisted.masterVolume,
        musicVolume: persisted.musicVolume,
        sfxVolume: persisted.sfxVolume,
        muted: persisted.muted,
      });
      pulseLayer = createPulseLayer(context, {
        destination: graph.musicInput,
        voiceTone: (frequency, duration, type, value, slide, at) =>
          toneAt(frequency, duration, type, value, slide, pulseLayer.input, at),
        voiceNoise: (duration, value, filterFrequency, at) =>
          noiseAt(duration, value, filterFrequency, pulseLayer.input, at),
      });
      noiseBuffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let index = 0; index < noiseData.length; index += 1) noiseData[index] = nextNoise();
      AudioConstructor = window.Audio;
      return context;
    } catch {
      try { context?.close(); } catch {}
      context = null;
      graph = null;
      noiseBuffer = null;
      AudioConstructor = null;
      pulseLayer?.dispose();
      pulseLayer = null;
      return null;
    }
  }

  function releaseVoice(voice) {
    if (!activeVoices.delete(voice)) return;
    for (const node of voice.nodes) {
      try { node.disconnect(); } catch {}
    }
  }

  function stopVoice(voice) {
    if (!voice) return;
    voice.source.onended = null;
    try { voice.source.stop(); } catch {}
    releaseVoice(voice);
  }

  function stopVoices() {
    for (const voice of [...activeVoices]) stopVoice(voice);
  }

  function registerVoice(source, nodes) {
    while (activeVoices.size >= MAX_ACTIVE_VOICES) stopVoice(activeVoices.values().next().value);
    const voice = { source, nodes };
    activeVoices.add(voice);
    source.onended = () => releaseVoice(voice);
    return voice;
  }

  function envelope(node, gainValue, attack, release, duration, startAt) {
    const start = Math.max(context.currentTime, startAt);
    node.gain.cancelScheduledValues(start);
    node.gain.setValueAtTime(SILENCE, start);
    node.gain.exponentialRampToValueAtTime(Math.max(0.0002, gainValue), start + attack);
    node.gain.exponentialRampToValueAtTime(SILENCE, start + duration + release);
  }

  function toneAt(frequency, duration, type, gainValue, slide, destination, startAt) {
    if (!context || !destination || disposed || activeVoices.size >= MAX_ACTIVE_VOICES) return;
    const start = Math.max(context.currentTime, startAt);
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(20, frequency), start);
    if (slide) oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, frequency + slide), start + duration);
    oscillator.connect(gain);
    gain.connect(destination);
    envelope(gain, gainValue, Math.min(0.025, duration * 0.2), 0.08, duration, start);
    registerVoice(oscillator, [oscillator, gain]);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.1);
  }

  function noiseAt(duration, gainValue, filterFrequency, destination, startAt) {
    if (!context || !destination || !noiseBuffer || disposed || activeVoices.size >= MAX_ACTIVE_VOICES) return;
    const start = Math.max(context.currentTime, startAt);
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    envelope(gain, gainValue, 0.004, 0.06, duration, start);
    registerVoice(source, [source, filter, gain]);
    source.start(start, noiseCursor % (noiseBuffer.duration - 0.01));
    source.stop(start + duration + 0.08);
    noiseCursor = (noiseCursor + 0.173) % 1;
  }

  function sfxApi() {
    return {
      tone: (frequency, duration, type, gainValue, slide, delay = 0) =>
        toneAt(frequency, duration, type, gainValue, slide, graph.sfxInput, context.currentTime + delay),
      noise: (duration, gainValue, filterFrequency, delay = 0) =>
        noiseAt(duration, gainValue, filterFrequency, graph.sfxInput, context.currentTime + delay),
    };
  }

  function getTrack(mood) {
    const config = MOODS[mood];
    if (!AudioConstructor || !config || !graph) return null;
    const cached = tracks.get(mood);
    if (cached) return cached;
    let element;
    try {
      element = new AudioConstructor(`/audio/${config.file}`);
    } catch {
      return null;
    }
    element.loop = true;
    element.preload = 'auto';
    element.playsInline = true;
    element.volume = 1;
    const voiceGain = context.createGain();
    voiceGain.gain.value = SILENCE;
    let source = null;
    try {
      source = context.createMediaElementSource(element);
      source.connect(voiceGain);
    } catch {
      source = null;
    }
    const track = { element, source, voiceGain, routed: Boolean(source), level: 0, mood };
    voiceGain.connect(graph.musicInput);
    tracks.set(mood, track);
    return track;
  }

  function setTrackLevel(track, value, seconds = 0.12) {
    const level = Math.max(0, Math.min(1, value));
    track.level = level;
    if (track.routed) {
      const now = context.currentTime;
      track.voiceGain.gain.cancelScheduledValues(now);
      track.voiceGain.gain.setValueAtTime(Math.max(SILENCE, track.voiceGain.gain.value), now);
      track.voiceGain.gain.linearRampToValueAtTime(Math.max(SILENCE, level), now + Math.max(0.01, seconds));
    } else {
      track.element.volume = level;
    }
  }

  function stopTrack(track) {
    if (!track) return;
    track.level = 0;
    try { track.element.pause(); } catch {}
  }

  function clearTransition() {
    if (pollTimer !== null) window.clearInterval(pollTimer);
    pollTimer = null;
    for (const timer of transitionTimers) window.clearTimeout(timer);
    transitionTimers = new Set();
  }

  function scheduleStop(track, seconds) {
    if (!track) return;
    const timer = window.setTimeout(() => {
      transitionTimers.delete(timer);
      stopTrack(track);
    }, Math.max(0, seconds) * 1000);
    transitionTimers.add(timer);
  }

  /**
   * Equal-power fade on one routed voice gain. A curve that overlaps a curve still in flight throws
   * in Chrome, so an interrupted handover drops to a linear ramp instead of losing the transition.
   */
  function fadeVoice(track, curve, start, duration, from) {
    const now = context.currentTime;
    const param = track.voiceGain.gain;
    const floor = Math.max(SILENCE, Math.min(1, from));
    param.cancelScheduledValues(now);
    param.setValueAtTime(floor, now);
    try {
      param.setValueCurveAtTime(curve, start, duration);
      return true;
    } catch {
      param.cancelScheduledValues(now);
      param.setValueAtTime(floor, now);
      param.linearRampToValueAtTime(SILENCE, start + duration);
      return false;
    }
  }

  function startCrossfade(previous, next, duration) {
    const now = context.currentTime;
    const curves = equalPowerCurves(duration, context.sampleRate);
    const start = now + 0.03;
    if (next.routed) fadeVoice(next, curves.in, start, duration, SILENCE);
    else next.element.volume = 1;
    next.level = 1;
    if (previous && previous !== next) {
      previous.level = Math.max(0, Math.min(1, previous.level));
      if (previous.routed) fadeVoice(previous, curves.out, start, duration, previous.level);
      else setTrackLevel(previous, 0, duration);
      scheduleStop(previous, duration + 0.2);
    }
  }

  function waitForAudioStart(track, token, onStart) {
    const startedAt = performance.now();
    const poll = () => {
      if (disposed || token !== transitionToken) return;
      const flowing = !track.element.paused && track.element.currentTime > 0.01;
      if (flowing || performance.now() - startedAt > CROSSFADE_START_TIMEOUT_MS) {
        if (pollTimer !== null) window.clearInterval(pollTimer);
        pollTimer = null;
        onStart();
        return;
      }
      pollTimer = window.setInterval(poll, CROSSFADE_POLL_MS);
    };
    poll();
  }

  function stopFallback() {
    if (schedulerTimer !== null) window.clearInterval(schedulerTimer);
    schedulerTimer = null;
    if (fallbackGain && context) fallbackGain.gain.setTargetAtTime(SILENCE, context.currentTime, 0.08);
  }

  function stopPulse() {
    if (pulseTimer !== null) window.clearInterval(pulseTimer);
    pulseTimer = null;
    pulseLayer?.setMood(null);
  }

  /** Same lookahead clock as the fallback sequencer, so a tempo change lands on the next step. */
  function startPulse(mood) {
    if (pulseTimer !== null) window.clearInterval(pulseTimer);
    pulseTimer = null;
    if (!context || !pulseLayer || disposed || graph?.values.muted) return;
    if (!pulseLayer.setMood(mood)) return;
    pulseStep = 0;
    nextPulseTime = context.currentTime + 0.06;
    const pump = () => {
      if (!context || disposed) return;
      const stepSeconds = pulseLayer.secondsPerStep();
      if (!Number.isFinite(stepSeconds)) return;
      while (nextPulseTime < context.currentTime + SCHEDULE_AHEAD_SECONDS) {
        pulseLayer.schedule(pulseStep++, nextPulseTime);
        nextPulseTime += stepSeconds;
      }
    };
    pump();
    pulseTimer = window.setInterval(pump, LOOKAHEAD_MS);
  }

  function scheduleFallbackStep(step, startAt, config, mood) {
    const pulse = step % 8;
    const lead = note(config.root, config.scale, step * 3 + Math.floor(step / 8));
    const busy = config.combat;
    if (pulse === 0 || pulse === 4) toneAt(config.root / 2, 0.42, 'sine', config.bass, 0, fallbackGain, startAt);
    if (pulse === 2 || pulse === 6) noiseAt(0.08, config.bass * 0.32, 900, fallbackGain, startAt);
    if (busy && pulse % 2 === 1) noiseAt(0.05, config.bass * 0.2, 3200, fallbackGain, startAt);
    toneAt(lead, 0.28, busy ? 'sawtooth' : 'triangle', config.lead, 0, fallbackGain, startAt);
    if (pulse === 7) toneAt(lead * 0.5, 0.5, 'sine', config.bass * 0.5, 0, fallbackGain, startAt + 0.02);
    if (mood === 'defeat' && pulse === 0) toneAt(config.root / 4, 1.4, 'sine', config.bass * 0.8, 0, fallbackGain, startAt);
  }

  function startFallback(mood) {
    stopFallback();
    if (!context || !graph || disposed) return;
    const config = MOODS[mood] || MOODS.exploration;
    if (!fallbackGain) {
      fallbackGain = context.createGain();
      fallbackGain.gain.value = SILENCE;
      fallbackGain.connect(graph.musicInput);
    }
    fallbackGain.gain.cancelScheduledValues(context.currentTime);
    fallbackGain.gain.setTargetAtTime(config.combat ? 0.62 : 0.48, context.currentTime, 0.12);
    const stepSeconds = 60 / config.bpm / 2;
    fallbackStep = 0;
    nextStepTime = context.currentTime + 0.05;
    const pump = () => {
      if (!context || disposed) return;
      while (nextStepTime < context.currentTime + SCHEDULE_AHEAD_SECONDS) {
        scheduleFallbackStep(fallbackStep++, nextStepTime, config, mood);
        nextStepTime += stepSeconds;
      }
    };
    pump();
    schedulerTimer = window.setInterval(pump, LOOKAHEAD_MS);
  }

  function activateMood(mood) {
    const config = MOODS[mood] || MOODS.exploration;
    currentMood = mood;
    graph?.setCombat(config.combat);
    const next = getTrack(mood) || getTrack('exploration');
    if (!next) {
      clearTransition();
      currentTrack = null;
      startFallback(mood);
      stopPulse();
      return;
    }
    if (next === currentTrack && !next.element.paused) {
      if (next.level < 1) setTrackLevel(next, 1, 0.4);
      stopFallback();
      startPulse(mood);
      return;
    }
    const previous = currentTrack;
    const outgoing = previous && previous !== next ? previous : null;
    currentTrack = next;
    clearTransition();
    stopFallback();
    startPulse(mood);
    // The outgoing cue keeps playing for the length of the handover: silencing it here turned every
    // mood change into a hard cut followed by a fade-in of the new cue.
    for (const track of tracks.values()) {
      if (track === next || track === outgoing || track.element.paused) continue;
      setTrackLevel(track, 0, INTERRUPT_FADE_SECONDS);
      scheduleStop(track, INTERRUPT_FADE_SECONDS + 0.05);
    }
    const token = ++transitionToken;
    setTrackLevel(next, 0, 0.08);
    const play = next.element.play();
    if (play && typeof play.catch === 'function') {
      play.catch(() => {
        if (disposed || token !== transitionToken) return;
        if (currentTrack === next) currentTrack = null;
        startFallback(mood);
        stopPulse();
      });
    }
    waitForAudioStart(next, token, () => {
      if (disposed || token !== transitionToken) return;
      if (currentTrack !== next || next.element.paused) {
        startFallback(mood);
        stopPulse();
        return;
      }
      startCrossfade(outgoing, next, config.fade);
    });
  }

  async function unlock() {
    if (disposed) return false;
    const activeContext = ensureContext();
    if (!activeContext) return false;
    try {
      if (activeContext.state === 'suspended') await activeContext.resume();
      if (disposed) return false;
      unlocked = true;
      activateMood(currentMood);
      removeGestureUnlock();
      return true;
    } catch {
      unlocked = false;
      return false;
    }
  }

  function setMood(mood) {
    const next = MOODS[mood] ? mood : 'exploration';


    if (next === currentMood && (currentTrack || schedulerTimer)) return;
    currentMood = next;
    if (unlocked && context && graph && !graph.values.muted) activateMood(next);
    moodListener?.(next);
  }

  function setTension(value) {
    if (!graph) return 0;
    return graph.setTension(value);
  }

  function setIntensity(value) {
    if (!pulseLayer) return 0;
    return pulseLayer.setIntensity(value);
  }

  function setMasterVolume(value) {
    if (!graph) {
      persisted.masterVolume = clamp01(value, persisted.masterVolume);
      return persisted.masterVolume;
    }
    const result = graph.setMasterVolume(value);
    persist();
    notifyVolumes();
    return result;
  }

  function setMusicVolume(value) {
    if (!graph) {
      persisted.musicVolume = clamp01(value, persisted.musicVolume);
      return persisted.musicVolume;
    }
    const result = graph.setMusicVolume(value);
    persist();
    notifyVolumes();
    return result;
  }

  function setSfxVolume(value) {
    if (!graph) {
      persisted.sfxVolume = clamp01(value, persisted.sfxVolume);
      return persisted.sfxVolume;
    }
    const result = graph.setSfxVolume(value);
    persist();
    notifyVolumes();
    return result;
  }

  function setMuted(value) {
    const muted = graph ? graph.setMuted(value) : Boolean(value);
    restored = true;
    if (muted) {
      clearTransition();
      stopVoices();
      stopFallback();
      stopPulse();
      for (const track of tracks.values()) stopTrack(track);
      currentTrack = null;
    }
    persist();
    notifyVolumes();
    if (!muted && unlocked && context) activateMood(currentMood);
    return muted;
  }

  function toggleMute() {
    if (disposed) return graph ? graph.values.muted : Boolean(persisted.muted);
    return setMuted(!(graph ? graph.values.muted : Boolean(persisted.muted)));
  }
  // A backgrounded tab should not keep the score running on a machine nobody is looking at, and a
  // suspended context is the one pause that costs nothing to come back from: the mix, the mood and
  // the cue positions are all still exactly where they were.
  function setBackgrounded(backgrounded) {
    if (disposed) return false;
    const next = Boolean(backgrounded);
    if (next === backgroundedFlag) return backgroundedFlag;
    backgroundedFlag = next;
    if (!context) return backgroundedFlag;
    if (backgroundedFlag) context.suspend().catch(() => {});
    else if (unlocked) context.resume().catch(() => {});
    return backgroundedFlag;
  }

  function playSfx(name, intensity = 1) {
    if (!unlocked || !context || !graph || disposed || graph.values.muted) return;
    const key = SFX_ALIASES[name] || name;
    const player = SFX_PLAYERS[key];
    if (!player) return;
    const amount = Math.max(0.15, Math.min(1.5, Number.isFinite(intensity) ? intensity : 1));
    sfxCount += 1;
    lastSfx = name;
    lastSfxIntensity = amount;
    const duck = SFX_DUCK[key];
    if (duck) graph.duckMusic(duck * amount);
    player(sfxApi(), amount);
  }

  function snapshot() {
    const values = graph ? graph.values : { ...persisted, combat: false, tension: 0, ducking: 0 };
    return {
      unlocked,
      muted: values.muted,
      restored,
      masterVolume: values.masterVolume,
      musicVolume: values.musicVolume,
      sfxVolume: values.sfxVolume,
      tension: values.tension,
      ducking: values.ducking,
      combat: values.combat,
      limiterKnee: LIMITER_KNEE,
      contextState: context ? context.state : null,
      backgrounded: backgroundedFlag,
      mood: currentMood,
      track: currentTrack ? currentTrack.element.src.split('/').pop() : null,
      routed: Boolean(currentTrack?.routed),
      trackLevel: currentTrack ? currentTrack.level : 0,
      // Every loaded cue, so a handover can be checked as an overlap instead of a level on one node.
      tracks: [...tracks.values()].map((entry) => ({
        mood: entry.mood,
        file: entry.element.src.split('/').pop(),
        playing: !entry.element.paused,
        level: Number(entry.level.toFixed(3)),
        position: Number((Number.isFinite(entry.element.currentTime) ? entry.element.currentTime : 0).toFixed(2)),
      })),
      fallback: Boolean(schedulerTimer),
      pulse: {
        mood: pulseLayer ? pulseLayer.mood : null,
        intensity: pulseLayer ? Number(pulseLayer.intensity.toFixed(3)) : 0,
        level: pulseLayer ? Number(pulseLayer.level.toFixed(4)) : 0,
        steps: pulseLayer ? pulseLayer.steps : 0,
        notes: pulseLayer ? pulseLayer.notes : 0,
        stepSeconds: pulseLayer ? Number(pulseLayer.secondsPerStep().toFixed(3)) : 0,
        running: Boolean(pulseTimer),
      },
      voices: activeVoices.size,
      sfxCount,
      lastSfx,
      lastSfxIntensity,
      disposed,
    };
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    transitionToken += 1;
    clearTransition();
    stopFallback();
    stopPulse();
    stopVoices();
    for (const track of tracks.values()) {
      track.element.onerror = null;
      stopTrack(track);
      if (track.routed) {
        try { track.source.disconnect(); } catch {}
        try { track.voiceGain.disconnect(); } catch {}
      }
      track.element.src = '';
    }
    if (fallbackGain) {
      try { fallbackGain.disconnect(); } catch {}
      fallbackGain = null;
    }
    AudioConstructor = null;
    tracks.clear();
    if (context && context.state !== 'closed') context.close().catch(() => {});
    context = null;
    graph = null;
    currentTrack = null;
    noiseBuffer = null;
    pulseLayer?.dispose();
    pulseLayer = null;
    moodListener = null;
    volumeListener = null;
    removeGestureUnlock();
  }

  const gestureEvents = ['pointerdown', 'keydown', 'touchstart'];
  const onFirstGesture = () => { unlock().catch(() => {}); };
  function removeGestureUnlock() {
    if (typeof window === 'undefined') return;
    for (const event of gestureEvents) window.removeEventListener(event, onFirstGesture, true);
  }
  if (typeof window !== 'undefined' && options.autoUnlock !== false) {
    for (const event of gestureEvents) window.addEventListener(event, onFirstGesture, true);
  }

  return {
    unlock,
    setMood,
    setTension,
    setIntensity,
    playSfx,
    toggleMute,
    setMuted,
    setMasterVolume,
    setMusicVolume,
    setSfxVolume,
    setBackgrounded,
    snapshot,
    dispose,
    get onMood() { return moodListener; },
    set onMood(value) { moodListener = typeof value === 'function' ? value : null; },
    get onVolumes() { return volumeListener; },
    set onVolumes(value) { volumeListener = typeof value === 'function' ? value : null; },
  };
}
